import type { SignerConfig } from '../types/signers';

/**
 * Environment detection utilities for warning about unsafe usage patterns
 */

/**
 * Detect if code is running in a browser environment
 */
export function isBrowserEnvironment(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Check if we're in a Node.js server environment
 */
export function isNodeEnvironment(): boolean {
  return (
    typeof process !== 'undefined' && process.versions != null && process.versions.node != null
  );
}

/**
 * Validate baseUrl format
 */
export function validateBaseUrl(baseUrl?: string): void {
  if (!baseUrl) return; // undefined is ok, will use default

  if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
    throw new Error('baseUrl must be a valid HTTP(S) URL');
  }

  // Warn about http (not https)
  if (
    baseUrl.startsWith('http://') &&
    !baseUrl.includes('localhost') &&
    !baseUrl.includes('127.0.0.1')
  ) {
    console.warn(
      '[Emblem Security Warning] baseUrl uses HTTP instead of HTTPS. This is insecure for production use.'
    );
  }
}

/**
 * Validate Ethereum address format
 */
export function validateEthereumAddress(address: string): void {
  if (!address || typeof address !== 'string') {
    throw new Error('Address is required');
  }

  if (!address.startsWith('0x')) {
    throw new Error('Address must start with 0x');
  }

  if (!/^0x[0-9a-fA-F]{40}$/.test(address)) {
    throw new Error('Invalid Ethereum address format');
  }
}

/**
 * Validate vault ID
 */
export function validateVaultId(vaultId: string): void {
  if (!vaultId || typeof vaultId !== 'string') {
    throw new Error('vaultId is required');
  }

  if (vaultId.trim() === '') {
    throw new Error('vaultId cannot be empty');
  }
}

/**
 * Safe number conversion with bounds checking
 */
export function toSafeNumber(value: unknown, fieldName: string): number {
  const num = Number(value);

  if (!Number.isSafeInteger(num)) {
    throw new Error(
      `${fieldName} value ${value} exceeds safe integer range (max: ${Number.MAX_SAFE_INTEGER})`
    );
  }

  return num;
}

/**
 * Extended config with security options
 */
export interface SignerSecurityConfig extends SignerConfig {
  /**
   * Enable debug logging for security-related checks
   * @default false
   */
  debugSecurity?: boolean;
}

/**
 * Validate signer configuration
 */
export function validateSignerConfig(config: SignerSecurityConfig): void {
  // Validate auth: require at least one method
  const hasJwt = !!config.jwt;
  const hasGetJwt = typeof config.getJwt === 'function';
  const hasHeaders = typeof config.getAuthHeaders === 'function';
  const hasSdk = !!config.sdk && typeof config.sdk.getSession === 'function';
  const hasApiKey = !!config.apiKey;

  if (!hasJwt && !hasGetJwt && !hasHeaders && !hasSdk && !hasApiKey) {
    throw new Error('Authentication required: provide jwt, getJwt(), getAuthHeaders(), sdk, or apiKey');
  }

  // Validate baseUrl if provided
  if (config.baseUrl) {
    validateBaseUrl(config.baseUrl);
  }

  // Security audit logging
  if (config.debugSecurity) {
    console.log('[Emblem Security Debug]', {
      environment: isBrowserEnvironment() ? 'browser' : 'node',
      hasBaseUrl: !!config.baseUrl,
      timestamp: new Date().toISOString(),
    });
  }
}
